import { useEffect, useMemo, useState } from 'react';
import { Link as RouterLink, useSearchParams } from 'react-router-dom';
import { Alert, Box, Button, Chip, InputAdornment, Link, Skeleton, Stack, Tab, Tabs, TextField, Typography } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { APP_CATEGORIES, categoryLabel, client, type AppCategory, type Application, type Department, type Feature } from '../lib/api';
import { AppStatusChip } from '../components/StatusChip';
import { PageHeader, Section } from '../components/Page';
import { AppCover } from '../components/AppScreens';
import { tokens } from '../theme';

export function AppsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [departments, setDepartments] = useState<Department[]>([]);
  const [applications, setApplications] = useState<Application[]>([]);
  const [features, setFeatures] = useState<Feature[]>([]);
  const [tab, setTab] = useState(() => searchParams.get('dept') || 'all');
  const [category, setCategory] = useState<AppCategory | 'all'>('all');
  const [q, setQ] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    client
      .catalog()
      .then((data) => {
        setDepartments(data.departments);
        setApplications(data.applications);
        setFeatures(data.features);
        const dept = searchParams.get('dept');
        if (dept && data.departments.some((d) => d.id === dept || d.slug === dept)) {
          const match = data.departments.find((d) => d.id === dept || d.slug === dept)!;
          setTab(match.id);
        }
      })
      .catch((err) => setError(err instanceof Error ? err.message : 'Failed to load'))
      .finally(() => setLoading(false));
  }, []);

  function setDeptTab(next: string) {
    setTab(next);
    setSearchParams(
      (prev) => {
        const p = new URLSearchParams(prev);
        if (next === 'all') p.delete('dept');
        else p.set('dept', next);
        return p;
      },
      { replace: true },
    );
  }

  const filtered = useMemo(() => {
    const query = q.trim().toLowerCase();
    return applications.filter((app) => {
      if (tab !== 'all' && app.department_id !== tab) return false;
      if (category !== 'all' && (app.category ?? 'custom') !== category) return false;
      if (!query) return true;
      const featText = features
        .filter((f) => f.application_id === app.id)
        .map((f) => `${f.title} ${f.detail}`)
        .join(' ');
      return `${app.name} ${categoryLabel(app.category)} ${app.tagline} ${app.description} ${app.audience} ${app.stack} ${featText}`
        .toLowerCase()
        .includes(query);
    });
  }, [applications, features, q, tab, category]);

  const count = (deptId: string) =>
    deptId === 'all' ? applications.length : applications.filter((a) => a.department_id === deptId).length;

  return (
    <>
      <PageHeader
        title="Applications"
        subtitle="Tools built by IT Lab for the teams and processes that keep AST moving."
        artwork="/catalog-art.svg"
      />

      <Section py={{ xs: 3, md: 4 }} sx={{ pb: { xs: 7, md: 10 } }}>
        <Stack spacing={{ xs: 2.5, md: 3.5 }}>
          <Stack direction={{ xs: 'column', md: 'row' }} gap={2.5} alignItems={{ md: 'center' }} justifyContent="space-between">
            <Tabs
              value={departments.some((d) => d.id === tab) || tab === 'all' ? tab : 'all'}
              onChange={(_, v) => setDeptTab(v)}
              variant="scrollable"
              allowScrollButtonsMobile
              aria-label="Filter applications by department"
              sx={{ minHeight: 48, '& .MuiTab-root': { minHeight: 48, mr: { xs: 2, sm: 3 } } }}
            >
              <Tab value="all" label={`All ${count('all')}`} />
              {departments.map((d) => (
                <Tab key={d.id} value={d.id} label={`${d.name} ${count(d.id)}`} />
              ))}
            </Tabs>
            <TextField
              label="Search applications"
              placeholder="Name, feature or stack"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              type="search"
              size="small"
              sx={{ width: { xs: '100%', md: 310 }, flex: 'none' }}
              slotProps={{
                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                },
              }}
            />
          </Stack>

          <Stack direction="row" gap={1} flexWrap="wrap" role="group" aria-label="Filter applications by category">
            {[{ value: 'all' as const, label: 'All types' }, ...APP_CATEGORIES].map((c) => {
              const n = c.value === 'all' ? applications.length : applications.filter((a) => (a.category ?? 'custom') === c.value).length;
              const on = category === c.value;
              return (
                <Chip
                  key={c.value}
                  label={`${c.label} ${n}`}
                  onClick={() => setCategory(c.value)}
                  color={on ? 'primary' : 'default'}
                  variant={on ? 'filled' : 'outlined'}
                  aria-pressed={on}
                />
              );
            })}
          </Stack>

          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ borderTop: `1px solid ${tokens.divider}`, pt: 2 }}>
            <Typography fontSize={14} color="text.secondary">
              {loading ? 'Loading applications' : `${filtered.length} ${filtered.length === 1 ? 'application' : 'applications'}`}
            </Typography>
            {(q || tab !== 'all' || category !== 'all') && (
              <Button
                size="small"
                onClick={() => {
                  setQ('');
                  setDeptTab('all');
                  setCategory('all');
                }}
                sx={{ minHeight: 32 }}
              >
                Clear filters
              </Button>
            )}
          </Stack>

          {error && <Alert severity="error">{error}</Alert>}
          {loading && (
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))', lg: 'repeat(3, minmax(0, 1fr))' }, gap: 2.5 }}>
              {[1, 2, 3].map((i) => <Skeleton key={i} variant="rounded" height={430} />)}
            </Box>
          )}
          {!loading && !error && filtered.length === 0 && (
            <Box sx={{ py: { xs: 5, md: 8 }, textAlign: 'center', borderTop: `1px solid ${tokens.divider}`, borderBottom: `1px solid ${tokens.divider}` }}>
              <Typography fontWeight={700} fontSize={20} color={tokens.ink}>No applications found</Typography>
              <Typography color="text.secondary" mt={0.5}>Try another department or search term.</Typography>
              {(q || tab !== 'all' || category !== 'all') && (
                <Button
                  onClick={() => {
                    setQ('');
                    setDeptTab('all');
                    setCategory('all');
                  }}
                  sx={{ mt: 1 }}
                >
                  Show all applications
                </Button>
              )}
            </Box>
          )}
          {!loading && !error && filtered.length > 0 && (
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))', lg: 'repeat(3, minmax(0, 1fr))' }, gap: 2.5 }}>
              {filtered.map((app) => {
                const dept = departments.find((d) => d.id === app.department_id);
                const appFeatures = features.filter((f) => f.application_id === app.id).slice(0, 3);
                return (
                  <Box
                    component="article"
                    key={app.id}
                    sx={{
                      minWidth: 0,
                      display: 'flex',
                      flexDirection: 'column',
                      border: `1px solid ${tokens.border}`,
                      borderTop: `3px solid ${tokens.blue}`,
                      bgcolor: '#fff',
                      '&:hover': { borderColor: tokens.blue, borderTopColor: tokens.blue },
                      '&:focus-within': { outline: `3px solid ${tokens.focus}`, outlineOffset: 2 },
                    }}
                  >
                    <AppCover app={app} />
                    <Stack spacing={1.5} sx={{ p: { xs: 2.25, md: 2.75 }, flex: 1 }}>
                      <Stack direction="row" alignItems="center" justifyContent="space-between" gap={1}>
                        <Typography fontSize={13} color="text.secondary" noWrap>{categoryLabel(app.category)} · {dept?.name ?? '—'}</Typography>
                        <AppStatusChip status={app.status} />
                      </Stack>
                      <Box>
                        <Typography variant="h2" sx={{ fontSize: 23, lineHeight: 1.2 }}>
                          <Link component={RouterLink} to={`/apps/${app.slug}`} color="inherit" underline="hover" fontWeight="inherit">
                            {app.name}
                          </Link>
                        </Typography>
                        <Typography fontWeight={600} color="#344054" mt={0.75}>{app.tagline}</Typography>
                      </Box>
                      <Typography fontSize={14} color="text.secondary" sx={{ lineHeight: 1.65 }}>
                        {app.description}
                      </Typography>
                      <Box component="ul" sx={{ listStyle: 'none', m: 0, p: 0, pt: 1, borderTop: `1px solid ${tokens.divider}` }}>
                        {appFeatures.map((feature) => (
                          <Box component="li" key={feature.id} sx={{ py: 0.5 }}>
                            <Typography fontSize={13} fontWeight={600} color={tokens.ink}>{feature.title}</Typography>
                          </Box>
                        ))}
                      </Box>
                      <Typography fontSize={12} color="text.secondary" sx={{ mt: 'auto !important' }}>
                        {app.stack.split('·').slice(0, 3).map((part) => part.trim()).filter(Boolean).join(' · ')}
                      </Typography>
                      <Stack direction="row" gap={1.5} alignItems="center" sx={{ pt: 1, borderTop: `1px solid ${tokens.divider}` }}>
                        {app.url ? (
                          <Button href={app.url} target="_blank" rel="noreferrer" variant="contained" size="small" endIcon={<OpenInNewIcon />}>
                            Open app
                          </Button>
                        ) : (
                          <Button component={RouterLink} to={`/apps/${app.slug}`} variant="contained" size="small">
                            View details
                          </Button>
                        )}
                        <Link component={RouterLink} to={app.url ? `/apps/${app.slug}` : '/status'} fontSize={14}>
                          {app.url ? 'Details' : 'Status'}
                        </Link>
                      </Stack>
                    </Stack>
                  </Box>
                );
              })}
            </Box>
          )}
        </Stack>
      </Section>

      <Section band="subtle" py={{ xs: 6, md: 9 }}>
        <Stack direction={{ xs: 'column', md: 'row' }} gap={3} alignItems={{ md: 'center' }} justifyContent="space-between">
          <Stack spacing={1}>
            <Typography variant="h2" fontSize={28}>
              Don’t see what you need?
            </Typography>
            <Typography fontSize={18} color="text.secondary">
              Tell us about the problem and we’ll scope it with you.
            </Typography>
          </Stack>
          <Button component={RouterLink} to="/request" variant="contained" size="large" sx={{ alignSelf: { xs: 'flex-start', md: 'center' } }}>
            Request an app
          </Button>
        </Stack>
      </Section>
    </>
  );
}
